// Search-Force Prediction —
//
// Two arm modes:
//
// 1. Settings-armed (static prediction). Magician toggles the switch in
//    Settings and configures a static date label + body template. From
//    the very first search-bar keystroke a fake "old" prediction note
//    appears at the top of the results with the configured date label
//    ("Vanochtend om 8:00 AM" by default) and `(xxx)` in the template
//    replaced by whatever the spectator typed.
//
// 2. Lockscreen-armed. Magician taps the camera quick-action on the
//    fake lockscreen and enters a passcode that decodes to a playing
//    card (same encoding as Apocalypse). The card + arm timestamp are
//    stored so the body template can substitute `(card)` with the
//    freshly-decoded card.
//
// Both modes share the SAME note-metadata date (the free-form
// `dateLabel` in Settings) so the note reads as pre-written no matter
// how it was armed. What changes between modes is only the body:
//   • Settings-armed → (card) collapses to empty
//   • Lockscreen-armed → (card) → the card label
// Independently of both arm modes, the body template also honours a
// `(date)` placeholder that is substituted at RENDER time with a
// localized timestamp computed as `now - dateOffsetMinutes`. That lets
// the magician write copy like "op (date) kom ik iemand tegen
// genaamd (xxx)…" where (date) reads like a natural time reference
// close to right-now.

import { STORAGE } from "./storage";

// Default body uses all placeholders so the magician can see what a
// full sentence looks like out of the box.
export const DEFAULT_SEARCH_FORCE_TEMPLATE =
  "Ik heb zo'n gevoel… ik heb zo'n vermoeden dat ik op (date) iemand tegenkom genaamd (xxx). Hij/zij is een (q1)-mens, houdt meer van (q2), verkiest (q3), en is eerder (q4). En denkt aan (card).";
export const DEFAULT_SEARCH_FORCE_TITLE = "Prediction";
// Older-looking default so the note reads as clearly pre-written even
// out of the box. The magician can override in Settings.
export const DEFAULT_SEARCH_FORCE_DATE_LABEL = "17 jan 2026 om 08:12";
// How many minutes to backdate the `(date)` placeholder from `now`.
// Default of 12 min feels "a few minutes ago" — like the magician
// jotted down the prediction shortly before the spectator arrived.
export const DEFAULT_SEARCH_FORCE_DATE_OFFSET_MINUTES = 12;

// Default word for each side of each personality question. Q1 = day
// preference, Q2 = pet preference, Q3 = drink preference, Q4 =
// social energy. Cancel is pushed when the magician taps the Cancel
// button; Emergency (now the keypad Erase key) when they tap Erase.
// All eight are freely editable in Settings.
export const DEFAULT_SEARCH_FORCE_Q1_CANCEL = "morning";
export const DEFAULT_SEARCH_FORCE_Q1_EMERGENCY = "evening";
export const DEFAULT_SEARCH_FORCE_Q2_CANCEL = "cats";
export const DEFAULT_SEARCH_FORCE_Q2_EMERGENCY = "dogs";
export const DEFAULT_SEARCH_FORCE_Q3_CANCEL = "tea";
export const DEFAULT_SEARCH_FORCE_Q3_EMERGENCY = "coffee";
export const DEFAULT_SEARCH_FORCE_Q4_CANCEL = "introvert";
export const DEFAULT_SEARCH_FORCE_Q4_EMERGENCY = "extrovert";

/** Read the full Search-Force configuration in one shot. */
export function getSearchForceConfig() {
  const rawOffset = localStorage.getItem(STORAGE.SEARCH_FORCE_DATE_OFFSET_MINUTES);
  const parsedOffset = parseInt(rawOffset ?? "", 10);
  return {
    enabled: localStorage.getItem(STORAGE.SEARCH_FORCE_ENABLED) === "1",
    template:
      localStorage.getItem(STORAGE.SEARCH_FORCE_TEMPLATE) ??
      DEFAULT_SEARCH_FORCE_TEMPLATE,
    title:
      localStorage.getItem(STORAGE.SEARCH_FORCE_TITLE) ??
      DEFAULT_SEARCH_FORCE_TITLE,
    dateLabel:
      localStorage.getItem(STORAGE.SEARCH_FORCE_DATE_LABEL) ??
      DEFAULT_SEARCH_FORCE_DATE_LABEL,
    // Structured "note date" ISO string. When present it wins over the
    // free-form dateLabel and drives BOTH the displayed header at the
    // top of the note AND the folder bucketing so both stay in sync.
    dateISO:
      localStorage.getItem(STORAGE.SEARCH_FORCE_DATE_ISO) || "",
    dateOffsetMinutes: Number.isFinite(parsedOffset)
      ? parsedOffset
      : DEFAULT_SEARCH_FORCE_DATE_OFFSET_MINUTES,
    q1CancelLabel:
      localStorage.getItem(STORAGE.SEARCH_FORCE_Q1_CANCEL_LABEL) ??
      DEFAULT_SEARCH_FORCE_Q1_CANCEL,
    q1EmergencyLabel:
      localStorage.getItem(STORAGE.SEARCH_FORCE_Q1_EMERGENCY_LABEL) ??
      DEFAULT_SEARCH_FORCE_Q1_EMERGENCY,
    q2CancelLabel:
      localStorage.getItem(STORAGE.SEARCH_FORCE_Q2_CANCEL_LABEL) ??
      DEFAULT_SEARCH_FORCE_Q2_CANCEL,
    q2EmergencyLabel:
      localStorage.getItem(STORAGE.SEARCH_FORCE_Q2_EMERGENCY_LABEL) ??
      DEFAULT_SEARCH_FORCE_Q2_EMERGENCY,
    q3CancelLabel:
      localStorage.getItem(STORAGE.SEARCH_FORCE_Q3_CANCEL_LABEL) ??
      DEFAULT_SEARCH_FORCE_Q3_CANCEL,
    q3EmergencyLabel:
      localStorage.getItem(STORAGE.SEARCH_FORCE_Q3_EMERGENCY_LABEL) ??
      DEFAULT_SEARCH_FORCE_Q3_EMERGENCY,
    q4CancelLabel:
      localStorage.getItem(STORAGE.SEARCH_FORCE_Q4_CANCEL_LABEL) ??
      DEFAULT_SEARCH_FORCE_Q4_CANCEL,
    q4EmergencyLabel:
      localStorage.getItem(STORAGE.SEARCH_FORCE_Q4_EMERGENCY_LABEL) ??
      DEFAULT_SEARCH_FORCE_Q4_EMERGENCY,
  };
}

export function saveSearchForceEnabled(v) {
  localStorage.setItem(STORAGE.SEARCH_FORCE_ENABLED, v ? "1" : "0");
  _scheduleSnapshotSync();
}
export function saveSearchForceTemplate(s) {
  localStorage.setItem(STORAGE.SEARCH_FORCE_TEMPLATE, String(s ?? ""));
  _scheduleSnapshotSync();
}
export function saveSearchForceTitle(s) {
  localStorage.setItem(STORAGE.SEARCH_FORCE_TITLE, String(s ?? ""));
  _scheduleSnapshotSync();
}
export function saveSearchForceDateLabel(s) {
  localStorage.setItem(STORAGE.SEARCH_FORCE_DATE_LABEL, String(s ?? ""));
  _scheduleSnapshotSync();
}
/**
 * Persist the structured "note date (top of note)" as an ISO string.
 * Empty string clears it so getSearchForceConfig() falls back to the
 * legacy free-form dateLabel. Ignores obviously-invalid input.
 */
export function saveSearchForceDateISO(iso) {
  const s = String(iso ?? "").trim();
  if (!s) {
    localStorage.removeItem(STORAGE.SEARCH_FORCE_DATE_ISO);
    _scheduleSnapshotSync();
    return;
  }
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return;
  localStorage.setItem(STORAGE.SEARCH_FORCE_DATE_ISO, d.toISOString());
  _scheduleSnapshotSync();
}

/**
 * Format a Date as a natural short "note-date" string, auto-localized
 * to the current app language. NL renders "14 apr om 10:31", EN
 * renders "14 Apr at 10:31". Returns "" for invalid input so callers
 * can fall through to any legacy free-form dateLabel.
 */
export function formatSearchForceNoteDate(date, locale, langCode) {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return "";
  try {
    const day  = date.toLocaleDateString(locale, { day: "numeric" });
    const mon  = date.toLocaleDateString(locale, { month: "short" }).replace(/\.$/, "");
    const time = date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
    const joiner = String(langCode || "").toLowerCase().startsWith("nl") ? "om" : "at";
    return `${day} ${mon} ${joiner} ${time}`;
  } catch {
    return "";
  }
}
export function saveSearchForceDateOffset(n) {
  const v = parseInt(n, 10);
  if (!Number.isFinite(v)) return;
  // Clamp to a sensible range — 0 (right now) up to a full day.
  const clamped = Math.max(0, Math.min(1440, v));
  localStorage.setItem(STORAGE.SEARCH_FORCE_DATE_OFFSET_MINUTES, String(clamped));
  _scheduleSnapshotSync();
}

// --- Per-question label setters. Each pair (cancel/emergency) drives
//     one of the three personality questions asked by the magician
//     while arming Search-Force from the lockscreen. ---------------
export function saveSearchForceQ1CancelLabel(s) {
  localStorage.setItem(STORAGE.SEARCH_FORCE_Q1_CANCEL_LABEL, String(s ?? ""));
  _scheduleSnapshotSync();
}
export function saveSearchForceQ1EmergencyLabel(s) {
  localStorage.setItem(STORAGE.SEARCH_FORCE_Q1_EMERGENCY_LABEL, String(s ?? ""));
  _scheduleSnapshotSync();
}
export function saveSearchForceQ2CancelLabel(s) {
  localStorage.setItem(STORAGE.SEARCH_FORCE_Q2_CANCEL_LABEL, String(s ?? ""));
  _scheduleSnapshotSync();
}
export function saveSearchForceQ2EmergencyLabel(s) {
  localStorage.setItem(STORAGE.SEARCH_FORCE_Q2_EMERGENCY_LABEL, String(s ?? ""));
  _scheduleSnapshotSync();
}
export function saveSearchForceQ3CancelLabel(s) {
  localStorage.setItem(STORAGE.SEARCH_FORCE_Q3_CANCEL_LABEL, String(s ?? ""));
  _scheduleSnapshotSync();
}
export function saveSearchForceQ3EmergencyLabel(s) {
  localStorage.setItem(STORAGE.SEARCH_FORCE_Q3_EMERGENCY_LABEL, String(s ?? ""));
  _scheduleSnapshotSync();
}
export function saveSearchForceQ4CancelLabel(s) {
  localStorage.setItem(STORAGE.SEARCH_FORCE_Q4_CANCEL_LABEL, String(s ?? ""));
  _scheduleSnapshotSync();
}
export function saveSearchForceQ4EmergencyLabel(s) {
  localStorage.setItem(STORAGE.SEARCH_FORCE_Q4_EMERGENCY_LABEL, String(s ?? ""));
  _scheduleSnapshotSync();
}

/** Persist the 4-element answers array captured on the lockscreen. */
export function setSearchForceAnswers(answers) {
  if (!Array.isArray(answers)) return;
  const clean = answers.slice(0, 4).map((a) => String(a ?? ""));
  localStorage.setItem(STORAGE.SEARCH_FORCE_ANSWERS, JSON.stringify(clean));
  _scheduleSnapshotSync();
}

/** Read the last-armed answers array, or an empty array. */
export function getSearchForceAnswers() {
  const raw = localStorage.getItem(STORAGE.SEARCH_FORCE_ANSWERS);
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return arr.map((a) => String(a ?? ""));
  } catch { /* corrupted — treat as empty */ }
  return [];
}

/** Wipe the stored answers array. */
export function clearSearchForceAnswers() {
  localStorage.removeItem(STORAGE.SEARCH_FORCE_ANSWERS);
}

/**
 * Read the currently-armed lockscreen card (if any). Returns
 * `{ card, armedAt }` or `null`. `card` is a `{value, suit}` object
 * (or a plain string for backwards-compat with pre-JSON entries) so
 * the caller can format the label in the app's active language.
 */
export function getSearchForceArmed() {
  const raw = localStorage.getItem(STORAGE.SEARCH_FORCE_CARD);
  const armedAtRaw = localStorage.getItem(STORAGE.SEARCH_FORCE_ARMED_AT);
  if (!raw || !armedAtRaw) return null;
  const armedAt = parseInt(armedAtRaw, 10);
  if (!Number.isFinite(armedAt) || armedAt <= 0) return null;
  let card = raw;
  // Prefer JSON — legacy label-strings still supported so existing
  // installs don't lose their armed state after this upgrade.
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.value && parsed.suit) {
      card = parsed;
    }
  } catch { /* legacy plain-string label — leave `card` as-is */ }
  return { card, armedAt };
}

/**
 * Arm a lockscreen-based Search-Force with the given card. Accepts a
 * `{value, suit}` object (preferred) or a plain string label (legacy).
 * The object form is stored as JSON so the caller can render the label
 * in any language via `apocalypseCardLabel`.
 */
export function setSearchForceArmed(card) {
  if (!card) return;
  const payload =
    typeof card === "object" && card.value && card.suit
      ? JSON.stringify({ value: card.value, suit: card.suit })
      : String(card).trim();
  if (!payload) return;
  localStorage.setItem(STORAGE.SEARCH_FORCE_CARD, payload);
  localStorage.setItem(STORAGE.SEARCH_FORCE_ARMED_AT, String(Date.now()));
  _scheduleSnapshotSync();
}

/** Fully unarm the lockscreen Search-Force. */
export function clearSearchForceArmed() {
  localStorage.removeItem(STORAGE.SEARCH_FORCE_CARD);
  localStorage.removeItem(STORAGE.SEARCH_FORCE_ARMED_AT);
  localStorage.removeItem(STORAGE.SEARCH_FORCE_ANSWERS);
  localStorage.removeItem(STORAGE.SEARCH_FORCE_FROZEN_DATE_ISO);
  localStorage.removeItem(STORAGE.SEARCH_FORCE_PERSISTED);
  _scheduleSnapshotSync();
}

/**
 * Freeze the moment the trick was armed as an ISO string in
 * localStorage. Uses `now - offsetMinutes` so the resulting timestamp
 * already reflects the "make the note look older" offset the magician
 * configured. Called from both arm sources (LockScreen decode +
 * Settings-enable toggle) so the (date) placeholder stays constant
 * across renders — a spectator opening the note two hours later still
 * sees the exact same time. Returns the frozen Date for convenience.
 */
export function setSearchForceFrozenDate(offsetMinutes) {
  const offset = Number.isFinite(offsetMinutes) ? Number(offsetMinutes) : 0;
  const frozen = new Date(Date.now() - offset * 60 * 1000);
  localStorage.setItem(STORAGE.SEARCH_FORCE_FROZEN_DATE_ISO, frozen.toISOString());
  return frozen;
}

/** Read the frozen arm-time Date (or null when no freeze is active). */
export function getSearchForceFrozenDate() {
  const raw = localStorage.getItem(STORAGE.SEARCH_FORCE_FROZEN_DATE_ISO);
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isFinite(d.getTime()) ? d : null;
}

/** Wipe the frozen arm-time so the next render falls back to live-now. */
export function clearSearchForceFrozenDate() {
  localStorage.removeItem(STORAGE.SEARCH_FORCE_FROZEN_DATE_ISO);
}

/**
 * Persist a fully-rendered prediction snapshot so it can be injected
 * into the folder list AFTER the spectator has opened it once. Shape:
 *   { title, body, dateISO, spectator, cardLabel }
 * Anything else is ignored. Returns true on success, false on invalid
 * input.
 */
export function setSearchForcePersisted(note) {
  if (!note || typeof note !== "object") return false;
  const payload = {
    title:      String(note.title      ?? ""),
    body:       String(note.body       ?? ""),
    dateISO:    String(note.dateISO    ?? ""),
    spectator:  String(note.spectator  ?? ""),
    cardLabel:  String(note.cardLabel  ?? ""),
  };
  if (!payload.body) return false;
  try {
    localStorage.setItem(STORAGE.SEARCH_FORCE_PERSISTED, JSON.stringify(payload));
    _scheduleSnapshotSync();
    return true;
  } catch { return false; }
}

/** Read the session-persisted prediction snapshot (or null). */
export function getSearchForcePersisted() {
  const raw = localStorage.getItem(STORAGE.SEARCH_FORCE_PERSISTED);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.body) return parsed;
    return null;
  } catch { return null; }
}

/** Remove the session-persisted prediction (called on new arm / disarm). */
export function clearSearchForcePersisted() {
  localStorage.removeItem(STORAGE.SEARCH_FORCE_PERSISTED);
}

/**
 * Substitute `(xxx)`, `(card)`, `(date)`, `(day)`, `(weekday)`, `(time)`,
 * `(q1)`, `(q2)`, `(q3)`, `(q4)` in `template`.
 *
 * • `(xxx)` — always fires when present; when absent we append the
 *   word so the magician still sees the spectator's utterance in the
 *   reveal.
 * • `(card)` — fires only when armed via the lockscreen; collapses to
 *   an empty string otherwise so the sentence still reads naturally.
 * • `(date)` — legacy combined day-and-time placeholder ("14 april 10:31").
 *   Still supported so older templates keep working.
 * • `(day)` — just the day-and-month portion ("14 april" / "April 14").
 *   Lets magicians write natural sentences like "op (day) om (time)".
 * • `(weekday)` — the localized weekday name ("maandag" / "Monday").
 *   Great for casual scripts like "op (weekday) rond (time)".
 * • `(time)` — just the clock portion ("10:31").
 * • `(q1)` / `(q2)` / `(q3)` / `(q4)` — substituted with the four
 *   answers collected via the Cancel/Erase taps during the lockscreen
 *   arm sequence. Collapse to empty strings when no answers are
 *   available (Settings-armed fallback or partial arm).
 */
export function buildSearchForceBody(
  template,
  word,
  card = "",
  dateStr = "",
  answers = [],
  dayStr = "",
  timeStr = "",
  weekdayStr = "",
) {
  const w = String(word || "").trim();
  const c = String(card || "").trim();
  const d = String(dateStr || "").trim();
  const day = String(dayStr || "").trim();
  const time = String(timeStr || "").trim();
  const weekday = String(weekdayStr || "").trim();
  const tpl = String(template || "").trim();
  if (!tpl) return w;
  let out = tpl;
  if (out.includes("(xxx)")) {
    out = out.replaceAll("(xxx)", w);
  } else {
    out = `${out} ${w}`.trim();
  }
  out = out.replaceAll("(card)", c);
  out = out.replaceAll("(date)", d);
  out = out.replaceAll("(weekday)", weekday);
  out = out.replaceAll("(day)", day);
  out = out.replaceAll("(time)", time);
  const safe = (v) => (v === undefined || v === null ? "" : String(v));
  out = out.replaceAll("(q1)", safe(answers[0]));
  out = out.replaceAll("(q2)", safe(answers[1]));
  out = out.replaceAll("(q3)", safe(answers[2]));
  out = out.replaceAll("(q4)", safe(answers[3]));
  return out;
}


// ------------------------------------------------------------------
// Prediction-link auto-sync helper. Every save/mutation above calls
// this so any changes propagate to the currently-active shared-note
// snapshot (POST /api/prediction-snapshot -> GET /n/{code}). Uses a
// dynamic import to avoid the circular dep predictionLink.js ↔
// searchForce.js. Debounces so rapid consecutive edits coalesce into
// a single backend PATCH. Silent no-op when no active snapshot.
// ------------------------------------------------------------------
let _syncTimer = null;
function _scheduleSnapshotSync() {
  if (typeof window === "undefined") return;
  if (_syncTimer) clearTimeout(_syncTimer);
  _syncTimer = setTimeout(async () => {
    _syncTimer = null;
    try {
      const mod = await import("./predictionLink");
      if (mod && typeof mod.syncActiveSnapshot === "function") {
        mod.syncActiveSnapshot();
      }
    } catch { /* offline / bundler issue — leave the local edit stand */ }
  }, 400);
}
